import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api.js';
import { localDB } from '../services/db.js';
import { detectPlatform } from '../lib/utils.js';

const AuthContext = createContext(null);

const friendlyDeviceName = () => {
  const platform = detectPlatform();
  const ua = navigator.userAgent || '';
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  return `${browser} on ${platform[0].toUpperCase()}${platform.slice(1)}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);

  const bootstrap = useCallback(async () => {
    setBootstrapping(true);

    // 1. Restore session from IndexedDB immediately — app renders instantly
    const session = await localDB.getSession();
    if (session) {
      setUser(session.user);
      setDevice(session.device);
      if (session.device?.id) {
        localStorage.setItem('syncvault_device_id', session.device.id.toString());
      }
    }

    // 2. End bootstrapping immediately — UI is now visible with cached data
    setBootstrapping(false);

    // 3. Background: refresh token if expired (fire-and-forget)
    if (session?.tokenExpiry && session.tokenExpiry < Date.now() && navigator.onLine) {
      api.refresh()
        .then(async (refreshRes) => {
          const { accessExpiresIn } = refreshRes.data;
          const newExpiry = Date.now() + (accessExpiresIn * 1000);
          await localDB.setSession({ ...session, tokenExpiry: newExpiry });
          console.log('[AuthContext] Proactive token refresh successful');
        })
        .catch((err) => {
          console.warn('[AuthContext] Proactive refresh failed:', err);
        });
    }

    // 4. Background: verify session with server (fire-and-forget)
    if (navigator.onLine) {
      api.me()
        .then(async (res) => {
          if (res?.data?.user) {
            setUser(res.data.user);
            setDevice(res.data.device);
            if (res.data.device?.id) {
              localStorage.setItem('syncvault_device_id', res.data.device.id.toString());
            }
            const currentSession = await localDB.getSession();
            await localDB.setSession({
              user: res.data.user,
              device: res.data.device,
              tokenExpiry: currentSession?.tokenExpiry
            });
          } else {
            await localDB.clearSession();
            setUser(null);
            setDevice(null);
          }
        })
        .catch(async (err) => {
          console.warn('[AuthContext] Background verification failed:', err);
          if (err.status === 401 || err.status === 403) {
            await localDB.clearSession();
            setUser(null);
            setDevice(null);
          }
        });
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Handle reconnection: Refresh token if it expired while offline
  useEffect(() => {
    const handleOnline = async () => {
      console.log('[AuthContext] Back online, checking session status...');
      const session = await localDB.getSession();
      if (session && session.tokenExpiry && session.tokenExpiry < Date.now()) {
        try {
          const refreshRes = await api.refresh();
          const { accessExpiresIn } = refreshRes.data;
          const newExpiry = Date.now() + (accessExpiresIn * 1000);
          
          await localDB.setSession({
            ...session,
            tokenExpiry: newExpiry
          });
          console.log('[AuthContext] Session refreshed after reconnection');
        } catch (err) {
          console.warn('[AuthContext] Refresh after reconnection failed:', err);
          if (err.status === 401 || err.status === 403) {
            await localDB.clearSession();
            setUser(null);
            setDevice(null);
          }
        }
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const login = useCallback(async ({ username, password, deviceName, deviceType, platform }) => {
    setLoading(true);
    try {
      const savedDeviceId = localStorage.getItem('syncvault_device_id');
      const res = await api.login({
        username, password,
        deviceId: savedDeviceId ? parseInt(savedDeviceId, 10) : undefined,
        deviceName: deviceName || friendlyDeviceName(),
        deviceType: deviceType || detectPlatform(),
        platform:   platform   || detectPlatform(),
      });
      
      const { user, device, accessExpiresIn } = res.data;
      const tokenExpiry = Date.now() + (accessExpiresIn * 1000);
      
      if (device?.id) {
        localStorage.setItem('syncvault_device_id', device.id.toString());
      }
      
      await localDB.setSession({
        user,
        device,
        tokenExpiry
      });

      setUser(user);
      setDevice(device);
      return res.data;
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    await localDB.clearSession();
    setUser(null);
    setDevice(null);
  }, []);

  const value = useMemo(() => ({
    user, device, loading, bootstrapping,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login, logout,
  }), [user, device, loading, bootstrapping, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
